/* eslint-disable react/no-unstable-nested-components */
import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { Dialog } from 'primereact/dialog';
import { Nullable } from 'primereact/ts-helpers';
import { ColumnProps } from 'primereact/column';
import { useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCalculator } from '@fortawesome/free-solid-svg-icons';

import {
  InputDateField,
  InputTextField,
  Button,
  Table,
  LoadingSpinner,
} from '@/components';
import CheckBox from '@/components/CheckBox/CheckBox';
import { DataBl } from '@/types/containersBLType';
import { DataLiquidateContainerBL } from '@/types/liquidateContainerBLType';
import { queryClient } from '@/providers/AppProvider';
import { formatDateAndTime } from '@/utils/formatDate';
import { useToastNotification, useMessage } from '@/hooks';
import { Messages } from 'primereact/messages';
import { classNames } from 'primereact/utils';

import styles from './AddContainerByBLModalImportFCL.module.scss';
import { getLiquidateContainerBlExport } from '../Depo20/services/getLiquidateContainerBlExport';

import InvoiceDetail from './InvoiceDetail';
import { addContainerBookingExport } from '../Depo20/services/addContainerBookingExport';
import { getValidatedContainerBooking } from '../Depo20/services/getValidatedContainerBooking';
import { getBookingList } from '../SearchByBooking/getBookingList';
import { estaEnLiquidacionProforma } from './services/estaEnLiquidacionProforma';
import { getOidVisita } from './services/getOidVisita';

import { necesitaPreguntarPorEarlyArrival } from './services/necesitaPreguntarPorEarlyArrival';
import {
  saveAndAddSettlement,
  SaveSettlementValidated,
} from '../Depo20/services/saveAndAddSettlement';

type ModalProps = {
  setVisibility: (visibility: boolean) => void;
  visible: boolean;
  oidLiquidacion: number;
  bodyOnSave: SaveSettlementValidated;
  validRut: () => Promise<boolean>;
  rut: string;
};

const AddContainerByBookingModalExportFCL: FC<ModalProps> = ({
  setVisibility,
  visible,
  oidLiquidacion,
  bodyOnSave,
  validRut,
  rut,
}) => {
  const { t } = useTranslation(['importFCL', 'exportFCL']);
  const { showMessage, elementRef, clearMessages } = useMessage();
  const { showToast } = useToastNotification();
  const [loading, setLoading] = useState(false);
  const [isLateArrival, setIsLateArrival] = useState(false);
  const [cutOffDate, setCutOffDate] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const [formValue, setFormValue] = useState({
    bl: '',
    fechaRetiro: null,
    lateArrival: false,
  });
  const [selectedItems, setSelectedItems] = useState([] as DataBl[]);
  const [amountDetails, setAmountDetails] = useState<
    DataLiquidateContainerBL[]
  >([]);

  const toggleDialog = () => {
    setVisibility(!visible);
  };

  const addContainers = useCallback(
    (currentOidLiquidacion: number) => {
      const promises = selectedItems.map(async (item) => {
        try {
          const { oidVisita } = item || {};
          const { lateArrival, fechaRetiro } = formValue;

          const body = {
            oidLiquidacion: currentOidLiquidacion,
            oidVisita,
            fechaIngreso: fechaRetiro || '',
            lateArrival: isLateArrival && lateArrival,
          };

          const res = await addContainerBookingExport(body);
          return res;
        } catch (err: unknown) {
          const { message } = err as { message: string };
          const container = (item?.contenedor || '').replace(' ', '');
          const newMessage = `${container}-${message}`;

          throw new Error(newMessage);
        }
      });

      return Promise.allSettled(promises);
    },
    [selectedItems, formValue, isLateArrival]
  );

  const { mutate: addContainersMutation, isPending: addingContainers } =
    useMutation({
      mutationFn: addContainers,
      onSuccess: (res) => {
        clearMessages();
        const count = res?.filter(
          ({ status }) => status === 'fulfilled'
        )?.length;
        if (count) {
          showToast({
            severity: 'success',
            detail: t('importFCL:containersAdded', { count }),
          });
        }

        const errors = res?.filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected'
        );
        errors.forEach(({ reason }) => {
          const [container, message] = (reason?.message || '').split(
            'importFCL:-'
          );
          showMessage({
            severity: 'error',
            summary: container,
            detail: message || t('importFCL:errorAddingContainers'),
          });
          showToast({
            severity: 'error',
            detail: container || t('importFCL:errorAddingContainers'),
          });
        });
        queryClient.invalidateQueries({
          queryKey: ['getImportFCLTable'],
        });
        setAmountDetails([]);
        if (count === res.length) toggleDialog();
      },
      onError: (error) => {
        clearMessages();
        showMessage({
          severity: 'error',
          detail: error?.message || t('importFCL:errorAddingContainers'),
        });
      },
      onSettled: () => {
        setLoading(false);
      },
    });

  const {
    mutate: getContainersMutation,
    data: containers = [],
    isPending: containersLoading,
  } = useMutation({
    mutationFn: getBookingList,
  });

  const processItems = async () => {
    try {
      const validations = (selectedItems || []).map(async (item) => {
        const { lateArrival, fechaRetiro } = formValue;
        const { oidVisita, contenedor } = item || {};

        const body = {
          oidVisita: oidVisita || 0,
          fecha: fechaRetiro || '',
          ingresoTardio: isLateArrival && lateArrival,
          rut,
          contenedor,
        };

        let response = await getValidatedContainerBooking({
          oidVisita,
          fecha: fechaRetiro || '',
        });

        if (!response) {
          const estaEnLiquidacion = await estaEnLiquidacionProforma({
            oidVisita,
          });
          response = {
            ok: !estaEnLiquidacion,
            mensaje: 'El contender se encuentra en liquidación.',
            okSpecified: true,
          };
        }

        return { response, item, body };
      });

      const results = await Promise.all(validations);
      clearMessages();

      const validItems = results.filter(
        ({ response }) => response?.ok || !response
      );
      const invalidItems = results.filter(({ response }) => !response?.ok);
      const newSelectedItems = validItems.map(({ item }) => item);

      setSelectedItems(newSelectedItems);

      invalidItems.forEach(({ response, body }) => {
        const message =
          typeof response === 'string' ? response : response?.mensaje;
        showMessage({
          severity: 'error',
          detail: message || '',
          summary: body?.contenedor,
        });
      });

      const calculateAmounts = validItems.map(async ({ body }) => {
        const response = await getLiquidateContainerBlExport(body);
        return response;
      });

      const newAmountDetails = await Promise.all(calculateAmounts);

      setAmountDetails(newAmountDetails);
    } catch (error) {
      clearMessages();
      showMessage({
        severity: 'error',
        detail: (error as { message: string })?.message as string,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (
    value: string | Nullable<Date> | boolean,
    name: string
  ) => {
    setFormValue({
      ...formValue,
      [name]: value,
    });
  };

  const dialogHeader = (
    <div className="text-blue-800">
      <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5rem' }} />
      {t('importFCL:addContainerByBLExport')}
    </div>
  );

  const onGetContainersButtonClick = () => {
    const { bl } = formValue;

    getContainersMutation({ bookingId: bl });
  };

  useEffect(() => {
    setAmountDetails([]);
  }, [selectedItems]);

  useEffect(() => {
    const fecha = formValue.fechaRetiro;
    setIsLateArrival(false);
    setCutOffDate('');

    if (!fecha) return;
    if (!selectedItems?.length) return;

    setLoading(true);

    const checkLateArrival = async () => {
      let hasLateArrival = false;
      let latestCutOffDate = '';

      const promises = selectedItems.map(async ({ contenedor, oidVisita }) => {
        const [marca, numero] = (contenedor || '').split(' ');
        const body = { fecha, oidVisita };

        try {
          const response = await getOidVisita({ marca, numero });
          const isLate = await necesitaPreguntarPorEarlyArrival(body);

          return {
            isLate,
            cutoff: response?.cutoff,
          };
        } catch (error) {
          return {
            isLate: false,
            cutoff: null,
          };
        }
      });

      const results = await Promise.all(promises);

      results.forEach(({ isLate, cutoff }) => {
        if (isLate) {
          hasLateArrival = true;
          if (cutoff) {
            const date = formatDateAndTime(cutoff);
            latestCutOffDate = date;
          }
        }
      });

      setIsLateArrival(hasLateArrival);
      if (hasLateArrival) {
        setCutOffDate(latestCutOffDate);
      } else {
        setFormValue((prevFormValue) => ({
          ...prevFormValue,
          lateArrival: false,
        }));
      }
      setLoading(false);
    };

    checkLateArrival();
  }, [formValue?.fechaRetiro, selectedItems]);

  const columns: ColumnProps[] = [
    {
      field: 'check',
      selectionMode: 'multiple',
      headerStyle: {
        width: '2rem',
        textAlign: 'center',
      },
    },

    {
      field: 'contenedor',
      header: t('importFCL:containerNumber'),
      body: (rowData) => {
        const value = (rowData?.contenedor || '')?.replace(' ', '');
        return <b>{value}</b>;
      },
    },
    {
      field: 'viajeCarga',
      header: t('importFCL:trip'),
      filter: true,
      sortable: true,
      showFilterMenu: true,
    },
    {
      field: 'armador',
      header: t('importFCL:shipowner'),
      filter: true,
      sortable: true,
      showFilterMenu: true,
    },
    {
      field: 'booking',
      header: t('importFCL:booking'),
      filter: true,
      sortable: true,
      showFilterMenu: true,
    },
    {
      field: 'liquidacionWeb',
      header: t('importFCL:settlement'),
      filter: true,
      sortable: true,
      showFilterMenu: true,
    },
    {
      field: 'playa',
      header: t('importFCL:beach'),
      filter: true,
      sortable: true,
      showFilterMenu: true,
    },
  ];

  const onCalculateButtonClick = () => {
    setLoading(true);
    processItems();
  };

  const { mutateAsync: saveData, isPending: savingSettlement } = useMutation({
    mutationFn: saveAndAddSettlement,
  });

  const onAddButtonClick = async () => {
    setLoading(true);
    const valid = await validRut();
    if (!valid) {
      setLoading(false);
      return;
    }
    if (!oidLiquidacion) {
      try {
        const settlementId = await saveData(bodyOnSave);
        addContainersMutation(settlementId);
        const newPath = `${location.pathname}?id=${settlementId}`;
        navigate(newPath, { replace: true });
        showToast({
          severity: 'success',
          detail: t('importFCL:settlementSaved'),
        });
      } catch (error) {
        showToast({
          severity: 'error',
          detail: t('importFCL:settlementNotSaved'),
        });
      }
      setLoading(false);
      return;
    }
    addContainersMutation(oidLiquidacion);
  };

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{
        maxWidth: window.innerWidth <= 1500 ? '95vw' : '1500px',
        minWidth: window.innerWidth <= 1200 ? '95vw' : '1432px',
        position: 'relative',
      }}
      onHide={toggleDialog}
      modal
      footer={
        <div
          className={` p-2 md:p-4 ${styles.contianerButtons} md:border-top-1 border-400 max-h-20rem overflow-auto`}
        >
          <Messages ref={elementRef} />
          <div className={`w-full flex justify-content-end ${styles.buttons} `}>
            <Button text onClick={toggleDialog} style={{ color: '#170a33' }}>
              {t('importFCL:cancel')}
            </Button>
            <Button
              onClick={onAddButtonClick}
              className={styles.containersButton}
              disabled={
                !amountDetails?.length || addingContainers || savingSettlement
              }
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              {t('importFCL:add')}
            </Button>
          </div>
        </div>
      }
    >
      {loading && (
        <div
          style={{ zIndex: 10, background: 'rgba(0,0,0, 0.2)' }}
          className="absolute top-0 bottom-0 left-0 right-0 z-10 flex align-items-center justify-content-center"
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="md:pb-4 px-2">
        <div className={`${styles.form} flex-column md:flex-row`}>
          <fieldset className="flex flex-column w-full align-items-start md:flex-row md:align-items-end">
            <InputTextField
              id="bl"
              className=" w-full mb-0 md:w-auto"
              name="bl"
              value={formValue.bl}
              onChange={(e) => handleFieldChange(e.target.value, 'bl')}
              label={t('exportFCL:booking')}
            />
            <Button
              onClick={onGetContainersButtonClick}
              className={` w-auto ${styles.containersButton}`}
              disabled={containersLoading}
            >
              {t('importFCL:displayContainers')}
            </Button>
          </fieldset>
          <fieldset className="flex flex-column justify-content-start w-full align-items-start md:flex-row md:align-items-center">
            <InputDateField
              className="w-auto"
              id="fechaRetiro"
              name="fechaRetiro"
              value={formValue.fechaRetiro}
              onChange={(e) => handleFieldChange(e, 'fechaRetiro')}
              label={t('exportFCL:withdrawalDateExport')}
            />
            {isLateArrival && (
              <CheckBox
                style={{ width: '100%', marginTop: '1.4rem' }}
                label={`${t('importFCL:lateEntry')} ${cutOffDate}`}
                checked={formValue.lateArrival}
                onChange={(e) => handleFieldChange(e, 'lateArrival')}
              />
            )}
          </fieldset>
        </div>
      </div>
      <div
        className={`${styles.content} flex-column md:flex-row md:border-top-1 border-400`}
      >
        <div
          className={`border-400 md:border-right-1 md:pr-4 md:w-8 ${styles.containers}`}
        >
          <h2 className="text-blue-800">{t('importFCL:containers')}</h2>
          <p>{t('importFCL:containersMessage')}:</p>
          <div>
            <div
              className={classNames(
                'p-3 rounded-t-md',
                styles.secondaryActionsWrapper
              )}
            >
              <b
                className={`hidden md:flex ${classNames(styles.containersSelected)}`}
              >
                {selectedItems?.length
                  ? `${t('importFCL:containersSelected')}: ${selectedItems?.length}`
                  : t('importFCL:noContainerSelected')}
              </b>
            </div>
            <Table
              columns={columns}
              data={containers}
              isLoading={containersLoading}
              selectedItems={selectedItems}
              onSelectionChange={(e) => setSelectedItems(e.value)}
              paginator
            />
          </div>
        </div>
        <div className={`mt-3 md:pl-4 md:mt-0 ${styles.amount}`}>
          <header
            className={`flex flex-column md:flex-row md:align-items-center gap-3 ${styles.amountHeader}`}
          >
            <div>
              <h2 className="text-blue-800 mb-2">
                {t('importFCL:amountTitle')}
              </h2>
              <p>{t('importFCL:amountMessage')}</p>
            </div>
            <Button
              type="button"
              onClick={onCalculateButtonClick}
              style={{
                width: 'fit-content',
                textWrap: 'nowrap',
                padding: '0.7rem 1.3rem',
              }}
              disabled={!formValue.fechaRetiro || !selectedItems?.length}
            >
              <FontAwesomeIcon icon={faCalculator} className="mr-2" />
              {t('importFCL:calculateAmount')}
            </Button>
          </header>
          {amountDetails?.map((item) => (
            <InvoiceDetail key={item.oid} data={item} />
          ))}
        </div>
      </div>
    </Dialog>
  );
};

export default AddContainerByBookingModalExportFCL;
